import { useEffect, useRef, useState } from "react";
import {
  useGetAppointmentsProgramsTherapistsMutation,
  useGetAppointmentsProgramsUnavailableTherapistsMutation,
} from "../services/api/user.api";
import { IAppointmentProgram, TAppointmentsProgramsTherapist } from "../types/types";

interface UseTherapistOptionsParams {
  form: IAppointmentProgram;
  usersGuid: string | undefined;
  adminCompanyId: number;
  adminGuid: string;
  showUnavailable: boolean;
  onResetTherapistGuid: () => void;
}

type TherapistOption = {
  value: number;
  label: string;
  therapistsGuid: string;
  therapistsId: number;
};

function useTherapistOptions({
  form,
  usersGuid = "0",
  adminCompanyId,
  adminGuid,
  showUnavailable,
  onResetTherapistGuid,
}: UseTherapistOptionsParams) {
  const [enabled, setEnabled] = useState(false);
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [appointmentsProgramsTherapist, setAppointmentsProgramsTherapist] = useState<TherapistOption[]>([]);
  const [appointmentsProgramsUnavailableTherapists, setAppointmentsProgramsUnavailableTherapists] = useState<TherapistOption[]>([]);
  
  // Use ref to store the callback to avoid it being in dependencies
  const onResetTherapistGuidRef = useRef(onResetTherapistGuid);
  
  // Update ref when callback changes
  useEffect(() => {
    onResetTherapistGuidRef.current = onResetTherapistGuid;
  }, [onResetTherapistGuid]);

  const [
    getAppointmentsProgramsTherapists,
    { isLoading: isGettingTherapists },
  ] = useGetAppointmentsProgramsTherapistsMutation();
  const [getAppointmentsProgramsUnavailableTherapists] =
    useGetAppointmentsProgramsUnavailableTherapistsMutation();

  // Transform response data to react-select options
  const transformTherapistsToOptions = (therapists: TAppointmentsProgramsTherapist[]): TherapistOption[] => {
    return therapists.map((therapist) => ({
      value: therapist.therapistsId,
      label: therapist.fullName,
      therapistsGuid: therapist.therapistsGuid,
      therapistsId: therapist.therapistsId,
    }));
  };

  useEffect(() => {
    onResetTherapistGuidRef.current();

    if (enabled) {
      setShowCheckboxes(true);

      if (showUnavailable) {
        getAppointmentsProgramsUnavailableTherapists({
          appointmentsDurationsId: form.appointmentsDurationsId,
          appointmentsSequenceId: form.appointmentsSequenceId,
          countOfAppointments: form.countOfAppointments,
          roleId: form.roleId,
          companyId: adminCompanyId,
          usersGuid,
          vetek: form.vetek,
          genderId: form.genderId,
          migzarId: form.migzarId,
          therapistsSpecializationsIds:
            Array.isArray(form.therapistsSpecializationsIds) &&
            form.therapistsSpecializationsIds.length === 0
              ? null
              : form.therapistsSpecializationsIds,
        })
          .unwrap()
          .then((data) => {
            setAppointmentsProgramsUnavailableTherapists(transformTherapistsToOptions(data));
          })
          .catch((error) => {
            // console.error("Error fetching unavailable therapists:", error);
            setAppointmentsProgramsUnavailableTherapists([]);
          });
      } else {
        getAppointmentsProgramsTherapists({
          appointmentsDurationsId: form.appointmentsDurationsId,
          appointmentsSequenceId: form.appointmentsSequenceId,
          countOfAppointments: form.countOfAppointments,
          roleId: form.roleId,
          companyId: adminCompanyId,
          usersGuid,
          vetek: form.vetek,
          genderId: form.genderId,
          migzarId: form.migzarId,
          therapistsSpecializationsIds:
            Array.isArray(form.therapistsSpecializationsIds) &&
            form.therapistsSpecializationsIds.length === 0
              ? null
              : form.therapistsSpecializationsIds,
        })
          .unwrap()
          .then((data) => {
            setAppointmentsProgramsTherapist(transformTherapistsToOptions(data));
          })
          .catch((error) => {
            // console.error("Error fetching therapists:", error);
            setAppointmentsProgramsTherapist([]);
          });
      }
    } else {
      setShowCheckboxes(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    form.appointmentsDurationsId,
    form.appointmentsSequenceId,
    form.countOfAppointments,
    form.roleId,
    usersGuid,
    adminCompanyId,
    form.vetek,
    form.genderId,
    form.migzarId,
    form.therapistsSpecializationsIds,
    showUnavailable,
    getAppointmentsProgramsTherapists,
    getAppointmentsProgramsUnavailableTherapists,
  ]);

  return { 
    enabled, 
    setEnabled, 
    showCheckboxes, 
    isGettingTherapists,
    appointmentsProgramsTherapist,
    appointmentsProgramsUnavailableTherapists,
  };
}

export default useTherapistOptions;

